import { audioAssetById, letterNames, phonemeById, wordById } from '../data/foundations'
import { getInstructionForLesson, getLessonAudioRequirements, isAudioAssetAvailable } from '../engine/audioReadiness'
import type { Lesson } from '../types/content'
import { audioAssetUrl } from './audioUrl'

export type PlaybackResult = 'ended' | 'unavailable' | 'interrupted'

export class AudioManager {
  private activeAudio: HTMLAudioElement | null = null
  // Keep the element that received the user's play gesture across all segments
  // and lessons. Preload elements only warm the cache; they are not new players.
  private player: HTMLAudioElement | null = null
  private playerAssetId: string | null = null
  private playerUrl: string | null = null
  private settle: ((result: PlaybackResult) => void) | null = null
  private requestId = 0
  private preloaded = new Map<string, HTMLAudioElement>()
  private checkedFiles = new Map<string, number>()
  private fileCheckControllers = new Set<AbortController>()

  stop() {
    this.requestId++
    this.settle?.('interrupted')
    if (this.activeAudio) { this.activeAudio.pause(); this.activeAudio.currentTime = 0 }
    this.activeAudio = null
  }

  cancelFileChecks() {
    for (const controller of this.fileCheckControllers) controller.abort()
    this.fileCheckControllers.clear()
  }

  private notifyMissing(assetId: string) {
    window.dispatchEvent(new CustomEvent('phonics-audio-missing', { detail: { assetId } }))
  }

  private async playSequence(assetIds: string[], onStep?: (index: number) => void): Promise<PlaybackResult> {
    this.stop()
    const request = this.requestId
    if (!assetIds.length) return 'unavailable'
    for (let index = 0; index < assetIds.length; index++) {
      if (request !== this.requestId) return 'interrupted'
      onStep?.(index)
      const result = await this.playSingle(assetIds[index], request)
      if (result !== 'ended') return result
      if (index < assetIds.length - 1) await new Promise((resolve) => window.setTimeout(resolve, 150))
    }
    return request === this.requestId ? 'ended' : 'interrupted'
  }

  private playSingle(assetId: string, request: number): Promise<PlaybackResult> {
    const asset = audioAssetById.get(assetId)
    if (!asset?.path || asset.status === 'missing' || asset.status === 'rejected') {
      this.notifyMissing(assetId)
      return Promise.resolve('unavailable')
    }
    return new Promise((resolve) => {
      const url = audioAssetUrl(assetId)!
      if (!this.player) {
        this.player = this.preloaded.get(assetId) ?? new Audio(url)
        this.preloaded.delete(assetId)
        this.playerUrl = url
      } else if (this.playerUrl !== url) {
        this.player.src = url
        this.playerUrl = url
      }
      this.playerAssetId = assetId
      const audio = this.player
      this.activeAudio = audio
      audio.currentTime = 0
      audio.volume = 0.85
      let resolved = false
      const finish = (result: PlaybackResult) => {
        if (resolved) return
        resolved = true
        window.clearTimeout(timeout)
        audio.removeEventListener('ended', ended)
        audio.removeEventListener('error', error)
        if (this.activeAudio === audio) { audio.pause(); this.activeAudio = null }
        if (this.settle === finish) this.settle = null
        resolve(result)
      }
      const ended = () => finish(request === this.requestId ? 'ended' : 'interrupted')
      const error = () => {
        if (request === this.requestId) this.notifyMissing(assetId)
        finish('unavailable')
      }
      const timeout = window.setTimeout(error, 30_000)
      this.settle = finish
      audio.addEventListener('ended', ended)
      audio.addEventListener('error', error)
      void audio.play().catch(error)
    })
  }

  preloadLesson(lesson: Lesson) {
    const { required, optional } = getLessonAudioRequirements(lesson)
    const wanted = new Set([...required, ...optional].filter(isAudioAssetAvailable))
    for (const [id, audio] of this.preloaded) if (!wanted.has(id)) {
      if (audio !== this.player) { audio.pause(); audio.removeAttribute?.('src'); audio.load() }
      this.preloaded.delete(id)
    }
    for (const id of wanted) {
      const asset = audioAssetById.get(id)
      if (!asset?.path || ['missing', 'rejected'].includes(asset.status) || this.preloaded.has(id)
        || (id === this.playerAssetId && this.playerUrl === audioAssetUrl(id))) continue
      const audio = new Audio(audioAssetUrl(id)!)
      audio.preload = 'auto'
      audio.load()
      this.preloaded.set(id, audio)
    }
  }

  /** A failed request is temporary and never changes the asset manifest or learning progress. */
  async checkLessonFiles(lesson: Lesson): Promise<string[]> {
    const { required } = getLessonAudioRequirements(lesson)
    const failures = await Promise.all(required.map(async (id) => {
      if (!isAudioAssetAvailable(id)) return id
      const url = audioAssetUrl(id)!
      if ((this.checkedFiles.get(url) ?? 0) > Date.now()) return undefined
      const controller = new AbortController()
      this.fileCheckControllers.add(controller)
      const timeout = globalThis.setTimeout(() => controller.abort(), 5_000)
      try {
        // GET permits normal HTTP/service-worker caches to keep lessons usable offline.
        // Unlike a HEAD-only check, a SPA fallback cannot masquerade as an audio file.
        const response = await fetch(url, { signal: controller.signal })
        if (!response.ok || response.headers.get('content-type')?.includes('text/html')) return id
        const bytes = new Uint8Array(await response.arrayBuffer())
        const signature = String.fromCharCode(...bytes.slice(0, 4))
        const wave = signature === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WAVE'
        const compressed = signature === 'OggS' || signature.startsWith('ID3') || (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0)
        if (bytes.length < 16 || (!wave && !compressed)) return id
        this.checkedFiles.set(url, Date.now() + 60_000)
        return undefined
      } catch {
        // Offline, timeout, and cancellation are retryable. Do not cache a failure.
        return id
      } finally {
        globalThis.clearTimeout(timeout)
        this.fileCheckControllers.delete(controller)
      }
    }))
    return failures.filter((id): id is string => Boolean(id))
  }

  playPhonemes(ids: string[], onStep?: (index: number) => void) {
    return this.playSequence(ids.map((id) => phonemeById.get(id)?.audioId ?? `phoneme_${id.replace(/^phoneme_/, '')}_ga`), onStep)
  }
  playPhoneme(id: string) { return this.playPhonemes([id]) }
  playLetterName(letter: string) {
    const entry = letterNames.find((item) => item.letter === letter.toLowerCase())
    return this.playSequence(entry ? [entry.audioId] : [])
  }
  playWord(id: string) {
    const word = wordById.get(id) ?? wordById.get(`word_${id}`)
    return this.playSequence(word ? [word.audioId] : [])
  }
  playBlendDemo(id: string) {
    const word = wordById.get(id) ?? wordById.get(`word_${id}`)
    return this.playSequence(word ? [word.blendDemoAudioId ?? word.audioId] : [])
  }
  // Deliberately separated sounds followed by the recorded word; not a natural blend demo.
  playSegmentedWord(id: string, onStep?: (index: number) => void) {
    const word = wordById.get(id)
    return this.playSequence(word ? [...word.phonemeIds.map((id) => phonemeById.get(id)?.audioId ?? ''), word.audioId] : [], onStep)
  }
  playInstruction(id = 'instruction_listen_zh') { return this.playSequence([id]) }
  playLessonInstruction(lesson: Lesson) { return this.playInstruction(getInstructionForLesson(lesson).id) }
  playSuccess() { return this.playSequence(['feedback_success']) }
}

export const audioManager = new AudioManager()
